import {
  Box,
  Button,
  Dropdown,
  DropdownLayoutValueOption,
  IconButton,
  Input,
  InputImperativeActions,
  Text,
} from '@wix/design-system'
import { useContent, useNext, usePageLoading } from '@wix/funnel-builder-lizard'
import { ArrowRight } from '@wix/wix-ui-icons-common/lazy'
import React, { ChangeEventHandler, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useFlowLogger } from '../../../bi/mfi'
import { DEVELOPER_OPTION_KEYS, useStorageContext } from '../../../context/StorageContext/StorageContext'
import { WixStudioTemplate } from '../../reusable/WixStudioTemplate/WixStudioTemplate'
import { getQuestionNameByIndex } from './bi'
import type { Content } from './Content'
import { MembersSelect } from './MembersSelect'
import { RoleSelect } from './RoleSelect'
import { SidePanel } from './SidePanel'
import { Step } from './Step'
import s from './WixStudioAgencyDetails.scss'

export type State = {
  currentIndex: number
  name: string
  role: string
  members: string
}

export const WixStudioAgencyDetails: FC = () => {
  const { content, biSlug } = useContent<Content>()
  const [isPageLoading] = usePageLoading()
  const flowLogger = useFlowLogger()
  const next = useNext()
  const inputRef = useRef<InputImperativeActions>(null)
  const dropDownRef = useRef<Dropdown>(null)
  const { save } = useStorageContext()

  const [state, setState] = useState({
    currentIndex: 1,
    name: '',
    role: '',
    members: '',
  })

  const { currentIndex, name, role, members } = state

  const isLastStep = useMemo(() => currentIndex >= 4, [currentIndex])
  const questionName = getQuestionNameByIndex(currentIndex, content)
  const pageLogger = useMemo(
    () => flowLogger.createPageLogger({ pageBiSlug: biSlug, questionName }),
    [flowLogger, biSlug, questionName],
  )

  const selectedRole = content.role.options.find(o => o.optionKey === role)
  const selectedMembersIndex = content.members.options.findIndex(o => o.optionKey === members)
  const selectedMembers = content.members.options[selectedMembersIndex]
  const roleTitle = selectedRole?.title
  const membersTitle = selectedMembers?.title
  const skipButton = useMemo(() => (isLastStep ? '' : content.skip), [content.skip, isLastStep])

  const isNewAgencyIntroUx = content.useNewUi

  const handleNameChange = useCallback<ChangeEventHandler<HTMLInputElement>>(e => {
    e.persist()
    setState(prev => ({ ...prev, name: e.target.value }))
  }, [])

  const goToStep = useCallback(
    (nextIndex: number) => {
      void pageLogger.back({ targetBiSlug: getQuestionNameByIndex(nextIndex, content) })
      setState(prev => ({ ...prev, currentIndex: nextIndex }))
    },
    [content, pageLogger],
  )

  const handleRoleSelect = useCallback(
    (optionKey: string) => {
      void pageLogger.answer({ answer: optionKey })
      setState(prev => ({ ...prev, role: optionKey, currentIndex: prev.currentIndex + 1 }))
    },
    [pageLogger],
  )

  const handleMembersSelect = useCallback(
    ({ optionKey }: { optionKey: string }) => {
      void pageLogger.answer({ answer: optionKey })
      setState(prev => ({ ...prev, members: optionKey, currentIndex: prev.currentIndex + 1 }))
    },
    [pageLogger],
  )

  const onMembersSelect = useCallback(
    (option: DropdownLayoutValueOption) => {
      setState(prev => ({ ...prev, members: option.id?.toString() }))
    },
    [pageLogger],
  )

  const onRoleSelect = useCallback(
    (option: DropdownLayoutValueOption) => {
      setState(prev => ({ ...prev, role: option.id?.toString() }))
    },
    [pageLogger],
  )

  const handleNameSelect = useCallback(() => {
    void pageLogger.answer({ answer: name })
    setState(prev => ({ ...prev, currentIndex: prev.currentIndex + 1 }))
  }, [pageLogger, name])

  const handleNextClick = useCallback(() => {
    if (isPageLoading) {
      return
    }

    if (isNewAgencyIntroUx) {
      void pageLogger.answer({
        answer: {
          name: name || null,
          role: role || null,
          members: members || null,
        },
      })
    } else {
      void pageLogger.answer({
        answer: {
          name: Boolean(name).toString(),
          role,
          members,
        },
      })
    }

    save({ agencyName: name, isDeveloper: DEVELOPER_OPTION_KEYS.includes(role) })

    next({
      [content.name.questionKey]: name ? { values: [`${!!name}`] } : { skipped: true },
      [content.role.questionKey]: role ? { values: [role] } : { skipped: true },
      [content.members.questionKey]: members ? { values: [members] } : { skipped: true },
    })
  }, [next, name, role, members, content, pageLogger, isPageLoading, save])

  const handleSkipClick = useCallback(() => {
    if (isNewAgencyIntroUx) {
      void pageLogger.answer({
        answer: {
          name: null,
          role: null,
          members: null,
        },
      })
      next({
        [content.name.questionKey]: { skipped: true },
        [content.role.questionKey]: { skipped: true },
        [content.members.questionKey]: { skipped: true },
      })
      return
    }
    setState(prev => ({
      ...prev,
      name: prev.currentIndex === 1 ? '' : prev.name,
      members: prev.currentIndex === 2 ? '' : prev.members,
      role: prev.currentIndex === 3 ? '' : prev.role,
      currentIndex: prev.currentIndex + 1,
    }))
  }, [])

  useEffect(() => {
    inputRef.current?.focus()
    dropDownRef.current?.focus()
  }, [])

  const membersStepContent = {
    ...content.members.content,
    doneText: selectedMembers?.doneText || content.members.content.doneText,
  }
  const roleStepContent = {
    ...content.role.content,
    doneText: selectedRole?.doneText || content.role.content.doneText,
  }
  const sidePanelTexts = [
    content.name.content.sidePanelText,
    content.members.content.sidePanelText,
    content.role.content.sidePanelText,
  ]
  const sidePanelText = sidePanelTexts[currentIndex - 1]

  const isButtonDisabled = () => {
    if (isNewAgencyIntroUx) {
      return false
    }
    return currentIndex !== 4
  }

  return (
    <WixStudioTemplate
      isPageLoading={isPageLoading}
      pageLogger={pageLogger}
      title={content.title}
      subTitleBelow={content.subtitle}
      backButton={content.back}
      skipButton={skipButton}
      skipButtonClassName={s.WixStudioAgencyDetails__skipButton}
      onSkipClick={handleSkipClick}
      sidePanel={
        <SidePanel
          heading={name || content.sidePanelNamePlaceholder}
          role={roleTitle || content.sidePanelRolePlaceholder}
          sitesLabel={content.sidePanelSitesLabel}
          membersLabel={content.sidePanelMembersLabel}
          membersPlaceholder={content.sidePanelMembersPlaceholder}
          members={membersTitle}
          memberCircles={selectedMembersIndex + 1}
          text={sidePanelText}
        />
      }
    >
      <Box direction="vertical" minHeight="489px" marginTop="4px">
        {isNewAgencyIntroUx ? (
          <Box direction="vertical" verticalAlign="middle" marginTop="36px" gap="SP6">
            <Box maxWidth="366px" direction="vertical" flex="1" gap="SP1">
              <Text secondary>{content.role.content.title}</Text>
              <Dropdown
                maxHeightPixels="325px"
                className={s.WixStudioAgencyDetails__dropdown}
                ref={dropDownRef}
                options={content.role.options.map(({ optionKey, title }) => ({
                  id: optionKey,
                  value: title,
                }))}
                value={role}
                onSelect={onRoleSelect}
                placeholder={content.role.content.placeholder}
              />
            </Box>

            <Box maxWidth="366px" direction="vertical" flex="1" gap="SP1">
              <Text secondary>{content.members.content.title}</Text>
              <Dropdown
                value={members}
                onSelect={onMembersSelect}
                placeholder={content.members.content.placeholder}
                options={content.members.options.map(({ optionKey, title }) => ({
                  id: optionKey,
                  value: title,
                }))}
              />
            </Box>

            <Box maxWidth="366px" direction="vertical" flex="1" gap="SP1">
              <Text secondary>{content.name.content.title}</Text>
              <Input
                value={name}
                onChange={handleNameChange}
                placeholder={content.name.content.placeholder}
                onEnterPressed={handleNameSelect}
              />
            </Box>
          </Box>
        ) : (
          <Box direction="vertical">
            <Step
              state={getState(1, currentIndex)}
              index={1}
              content={content.name.content}
              answer={name}
              onClick={goToStep}
            >
              <Box verticalAlign="middle" gap="SP3">
                <Box maxWidth="366px" direction="vertical" flex="1">
                  <Input
                    value={name}
                    onChange={handleNameChange}
                    placeholder={content.name.content.placeholder}
                    onEnterPressed={handleNameSelect}
                    ref={inputRef}
                  />
                </Box>
                <IconButton
                  dataHook="next-button"
                  onClick={handleNameSelect}
                  priority={name === '' ? 'secondary' : 'primary'}
                >
                  <ArrowRight />
                </IconButton>
              </Box>
            </Step>
            <Step
              state={getState(2, currentIndex)}
              index={2}
              content={membersStepContent}
              answer={membersTitle}
              onClick={goToStep}
            >
              <MembersSelect options={content.members.options} value={members} onSelect={handleMembersSelect} />
            </Step>
            <Step
              state={getState(3, currentIndex)}
              index={3}
              content={roleStepContent}
              answer={roleTitle}
              onClick={goToStep}
            >
              <RoleSelect options={content.role.options} value={role} onSelect={handleRoleSelect} />
            </Step>
          </Box>
        )}
        <Box marginTop="60px">
          <Button suffixIcon={<ArrowRight />} onClick={handleNextClick} disabled={isButtonDisabled()} size="medium">
            {content.next}
          </Button>
        </Box>
      </Box>
    </WixStudioTemplate>
  )
}

const getState = (index: number, currentIndex: number) => {
  if (index === currentIndex) {
    return 'active'
  }
  if (index < currentIndex) {
    return 'complete'
  }
  return 'default'
}
